import React, { useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
    Text,
    Paper,
    Stack,
    SimpleGrid,
    Button,
    Progress,
    Group,
    Loader,
    ThemeIcon,
    RingProgress,
    Center,
    Flex,
} from '@mantine/core';
import {
    IconRefresh,
    IconCamera,
    IconServer,
    IconBrandJavascript,
    IconCpu,
    IconDatabase,
    IconStack2,
    IconGauge,
    IconHeartRateMonitor,
} from '@tabler/icons-react';
import { GET_MEMORY_STATS } from '../../apollo/queries/memoryDiagnostics';
import { CAPTURE_HEAP_SNAPSHOT } from '../../apollo/mutations/memoryDiagnostics';
import {
    showErrorNotification,
    showSuccessNotification,
} from '../../notifications';
import { MaintenanceTabPanelHeader } from '../common/MaintenanceTabPanelHeader';

interface MemoryStats {
    totalProcessMemoryMb: number;
    javascriptHeapUsedMb: number;
    javascriptHeapTotalMb: number;
    nativeMemoryMb: number;
    externalMemoryMb: number;
    arrayBuffersMb: number;
    heapSizeLimitMb: number;
    availableHeapMb: number;
    uptimeSeconds: number;
    containerLimitMb: number;
}

function formatUptime(seconds: number): string {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    const parts: string[] = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    parts.push(`${minutes}m`);
    return parts.join(' ');
}

function getMemoryColor(percentage: number): string {
    if (percentage > 80) return 'red';
    if (percentage > 60) return 'orange';
    return 'blue';
}

function StatCard({
    label,
    value,
    suffix = 'MB',
    color = 'blue',
    icon,
    description,
}: {
    label: string;
    value: number | string;
    suffix?: string;
    color?: string;
    icon: React.ReactNode;
    description?: string;
}) {
    return (
        <Paper
            p="lg"
            withBorder
            radius="lg"
            style={{ position: 'relative', overflow: 'hidden' }}
        >
            <div
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: 4,
                    height: '100%',
                    backgroundColor: `var(--mantine-color-${color}-6)`,
                    borderRadius: '4px 0 0 4px',
                }}
            />
            <Group gap="md" wrap="nowrap">
                <ThemeIcon size={44} radius="md" variant="light" color={color}>
                    {icon}
                </ThemeIcon>
                <div style={{ flex: 1 }}>
                    <Text size="xs" tt="uppercase" fw={600} c="dimmed" mb={2}>
                        {label}
                    </Text>
                    <Text size="xl" fw={700} lh={1}>
                        {typeof value === 'number'
                            ? value.toLocaleString(undefined, {
                                  maximumFractionDigits: 1,
                              })
                            : value}{' '}
                        <Text span size="sm" fw={500} c="dimmed">
                            {suffix}
                        </Text>
                    </Text>
                    {description && (
                        <Text size="xs" c="dimmed" mt={4}>
                            {description}
                        </Text>
                    )}
                </div>
            </Group>
        </Paper>
    );
}

export function MemoryDiagnostics() {
    const { data, loading, error, refetch } = useQuery(GET_MEMORY_STATS, {
        fetchPolicy: 'network-only',
        pollInterval: 30000,
    });

    const [captureSnapshot, { loading: capturing }] = useMutation(
        CAPTURE_HEAP_SNAPSHOT,
        {
            onCompleted: result => {
                if (result.captureHeapSnapshot.uploaded) {
                    showSuccessNotification(
                        `Heap snapshot uploaded to S3: ${result.captureHeapSnapshot.key}`,
                    );
                } else {
                    showErrorNotification(
                        result.captureHeapSnapshot.error ||
                            'Failed to capture snapshot',
                    );
                }
            },
            onError: err => {
                showErrorNotification(`Snapshot failed: ${err.message}`);
            },
        },
    );

    useEffect(() => {
        if (error) {
            showErrorNotification(
                `Failed to load memory stats: ${error.message}`,
            );
        }
    }, [error]);

    const stats: MemoryStats | null = data?.getMemoryStats || null;

    if (loading && !stats) {
        return (
            <Paper p="xl" withBorder>
                <Group justify="center">
                    <Loader size="sm" />
                    <Text c="dimmed">Loading memory stats...</Text>
                </Group>
            </Paper>
        );
    }

    if (!stats) {
        return (
            <Paper p="md" withBorder>
                <Text c="dimmed" ta="center">
                    Unable to load memory statistics.
                </Text>
            </Paper>
        );
    }

    const processUsagePercent =
        (stats.totalProcessMemoryMb / stats.containerLimitMb) * 100;
    const heapUsagePercent =
        (stats.javascriptHeapUsedMb / stats.heapSizeLimitMb) * 100;
    const processColor = getMemoryColor(processUsagePercent);
    const heapColor = getMemoryColor(heapUsagePercent);

    return (
        <div>
            <MaintenanceTabPanelHeader>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 16,
                    }}
                >
                    <Stack gap="md">
                        <Group gap="xs">
                            <IconHeartRateMonitor size={24} />
                            <Text size="lg" fw={500}>
                                API Memory Diagnostics
                            </Text>
                        </Group>
                        <Text size="sm" c="dimmed">
                            Live memory usage of the production API. Stats
                            auto-refresh every 30 seconds. Use the heap snapshot
                            button to capture a snapshot for debugging memory
                            issues.
                        </Text>
                    </Stack>
                    <Flex gap="xs">
                        <Button
                            variant="light"
                            size="xs"
                            leftSection={<IconRefresh size={14} />}
                            onClick={() => refetch()}
                            loading={loading}
                            fullWidth={false}
                            w="150px"
                        >
                            Refresh
                        </Button>
                        <Button
                            variant="light"
                            size="xs"
                            color="orange"
                            leftSection={<IconCamera size={14} />}
                            onClick={() => captureSnapshot()}
                            loading={capturing}
                            fullWidth={false}
                            w="150px"
                        >
                            Heap Snapshot
                        </Button>
                    </Flex>
                </div>
            </MaintenanceTabPanelHeader>

            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md" mb="md">
                <Paper p="lg" withBorder radius="lg">
                    <Group wrap="nowrap" gap="lg">
                        <RingProgress
                            size={100}
                            thickness={10}
                            roundCaps
                            sections={[
                                {
                                    value: processUsagePercent,
                                    color: processColor,
                                },
                            ]}
                            label={
                                <Center>
                                    <Text size="sm" fw={700} c={processColor}>
                                        {processUsagePercent.toFixed(0)}%
                                    </Text>
                                </Center>
                            }
                        />
                        <div style={{ flex: 1 }}>
                            <Text size="sm" fw={600} mb={4}>
                                Total Process Memory
                            </Text>
                            <Text size="xl" fw={700} lh={1} mb={8}>
                                {stats.totalProcessMemoryMb.toLocaleString()} MB
                            </Text>
                            <Progress
                                value={processUsagePercent}
                                color={processColor}
                                size="sm"
                                radius="xl"
                            />
                            <Text size="xs" c="dimmed" mt={4}>
                                of {(stats.containerLimitMb / 1024).toFixed(0)}{' '}
                                GB container limit
                            </Text>
                        </div>
                    </Group>
                </Paper>

                <Paper p="lg" withBorder radius="lg">
                    <Group wrap="nowrap" gap="lg">
                        <RingProgress
                            size={100}
                            thickness={10}
                            roundCaps
                            sections={[
                                {
                                    value: heapUsagePercent,
                                    color: heapColor,
                                },
                            ]}
                            label={
                                <Center>
                                    <Text size="sm" fw={700} c={heapColor}>
                                        {heapUsagePercent.toFixed(0)}%
                                    </Text>
                                </Center>
                            }
                        />
                        <div style={{ flex: 1 }}>
                            <Text size="sm" fw={600} mb={4}>
                                JavaScript Heap
                            </Text>
                            <Text size="xl" fw={700} lh={1} mb={8}>
                                {stats.javascriptHeapUsedMb.toLocaleString()} MB
                            </Text>
                            <Progress
                                value={heapUsagePercent}
                                color={heapColor}
                                size="sm"
                                radius="xl"
                            />
                            <Text size="xs" c="dimmed" mt={4}>
                                of {(stats.heapSizeLimitMb / 1024).toFixed(1)}{' '}
                                GB heap limit
                            </Text>
                        </div>
                    </Group>
                </Paper>
            </SimpleGrid>

            <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md" mb="md">
                <StatCard
                    label="Total Process"
                    value={stats.totalProcessMemoryMb}
                    color={processUsagePercent > 75 ? 'red' : 'blue'}
                    icon={<IconServer size={22} />}
                    description="All memory used by the Node.js process"
                />
                <StatCard
                    label="JavaScript Heap"
                    value={stats.javascriptHeapUsedMb}
                    color={heapUsagePercent > 75 ? 'red' : 'teal'}
                    icon={<IconBrandJavascript size={22} />}
                    description="V8 heap — JS objects, closures, strings"
                />
                <StatCard
                    label="Native Memory"
                    value={stats.nativeMemoryMb}
                    color={stats.nativeMemoryMb > 2048 ? 'orange' : 'violet'}
                    icon={<IconCpu size={22} />}
                    description="Prisma engine, native addons, Buffers"
                />
                <StatCard
                    label="External Memory"
                    value={stats.externalMemoryMb}
                    color="cyan"
                    icon={<IconDatabase size={22} />}
                    description="V8-tracked external allocations"
                />
                <StatCard
                    label="Array Buffers"
                    value={stats.arrayBuffersMb}
                    color="indigo"
                    icon={<IconStack2 size={22} />}
                    description="File reads, Excel generation, S3 uploads"
                />
                <StatCard
                    label="Available Heap"
                    value={stats.availableHeapMb}
                    color="green"
                    icon={<IconGauge size={22} />}
                    description="Remaining heap capacity before OOM"
                />
            </SimpleGrid>

            <Group justify="space-between" px="xs">
                <Text size="xs" c="dimmed">
                    Uptime: {formatUptime(stats.uptimeSeconds)}
                </Text>
                <Text size="xs" c="dimmed">
                    Auto-refreshes every 30s
                </Text>
            </Group>
        </div>
    );
}
